import React from "react";
import { NavLink, Link } from "react-router-dom";
import { Home, NotebookPen, TrendingUp, Leaf, Trophy, LifeBuoy, CalendarDays, MessageCircleHeart, Sparkles, UserRound } from "lucide-react";
import { PLAN_NAMES } from "@/mock";
import { InstallButton } from "@/components/InstallApp";

export const NAV = [
  { to: "/", label: "Accueil", icon: Home, id: "accueil" },
  { to: "/journal", label: "Journal", icon: NotebookPen, id: "journal" },
  { to: "/progres", label: "Progrès", icon: TrendingUp, id: "progres" },
  { to: "/solutions", label: "Solutions", icon: Leaf, id: "solutions" },
  { to: "/defis", label: "Défis", icon: Trophy, id: "defis" },
  { to: "/programme", label: "Programme", icon: CalendarDays, id: "programme" },
  { to: "/coach", label: "Coach IA", icon: MessageCircleHeart, id: "coach" },
];
const BOTTOM_LEFT = [NAV[0], NAV[1]];
const BOTTOM_RIGHT = [NAV[3], { to: "/compte", label: "Compte", icon: UserRound, id: "compte" }];
const PRICING_ITEM = { to: "/tarifs", label: "Tarifs", icon: Sparkles, id: "tarifs" };

export const Logo = () => (
  <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
    <span className="relative grid h-9 w-9 place-items-center rounded-full bg-[#2F4A3A]">
      <Leaf className="text-[#F2C46D]" size={18} />
    </span>
    <span className="font-display text-2xl font-semibold tracking-tight text-[#1F2A24]">Douceur</span>
  </Link>
);

function AccountAvatar({ user }) {
  if (user?.picture) return <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />;
  return <span className="grid h-7 w-7 place-items-center rounded-full bg-[#EAF0E6] text-[#2F4A3A]"><UserRound size={15} /></span>;
}

export function AccountButton({ user, plan }) {
  const label = user ? user.name?.split(" ")[0] || "Compte" : "Connexion";
  return (
    <Link to={user ? "/compte" : "/connexion"} data-testid="header-account-btn"
      className="hidden items-center gap-2 rounded-full border border-[#E9E1D6] bg-white py-1 pl-1 pr-3.5 text-sm font-medium transition-colors hover:bg-[#EFE7DC] md:inline-flex">
      <AccountAvatar user={user} />
      {label}
      {user && plan !== "free" && <span className="rounded-full bg-[#FBEFD6] px-2 text-[10px] font-semibold text-[#9A6B12]">{PLAN_NAMES[plan]}</span>}
    </Link>
  );
}

export function DesktopNav() {
  return (
    <nav className="hidden items-center gap-0.5 lg:flex">
      {NAV.map(({ to, label, id }) => (
        <NavLink key={to} to={to} end data-testid={`nav-${id}`}
          className={({ isActive }) => `rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${isActive ? "bg-[#2F4A3A] text-[#FBF7F2]" : "text-[#4A5A50] hover:bg-[#EFE7DC]"}`}>
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

export function HeaderLinks({ plan, showSOS }) {
  return (
    <>
      {plan === "free" && (
        <Link to="/tarifs" data-testid="header-pricing-btn" className="hidden items-center gap-1.5 rounded-full border border-[#EAD9B8] bg-[#FFF8EA] px-3.5 py-2 text-sm font-semibold text-[#9A6B12] transition-colors hover:bg-[#FBEFD6] sm:inline-flex">
          <Sparkles size={14} /> Tarifs
        </Link>
      )}
      {showSOS && (
        <Link to="/sos" data-testid="header-sos-btn"
          className="inline-flex min-h-10 items-center gap-2 rounded-full bg-[#E8735A] px-4 py-2 text-sm font-semibold text-white shadow-[0_8px_24px_-8px_rgba(232,115,90,.7)] transition-colors hover:bg-[#D9634A]">
          <LifeBuoy size={16} /> SOS
        </Link>
      )}
    </>
  );
}

export function MobileMenu({ user, onClose }) {
  const items = [...NAV, PRICING_ITEM, { to: user ? "/compte" : "/connexion", label: user ? "Mon compte" : "Connexion", icon: UserRound, id: "compte" }];
  return (
    <nav className="border-t border-[#E9E1D6] px-5 py-3 lg:hidden">
      {items.map(({ to, label, icon: Icon, id }) => (
        <NavLink key={id} to={to} end onClick={onClose} data-testid={`mobile-nav-${id}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium ${isActive ? "bg-[#2F4A3A] text-white" : "text-[#4A5A50]"}`}>
          <Icon size={18} /> {label}
        </NavLink>
      ))}
      <InstallButton testid="mobile-install-btn" className="mt-2 flex w-full items-center gap-3 rounded-xl bg-[#EAF0E6] px-3 py-3 text-sm font-semibold text-[#2F4A3A]" label="Installer l'application" />
    </nav>
  );
}

function BottomItem({ to, label, icon: Icon, id }) {
  return (
    <NavLink to={to} end data-testid={`bottom-nav-${id}`}
      className={({ isActive }) => `flex min-h-14 flex-col items-center justify-center gap-1 py-2 text-[11px] font-medium transition-colors ${isActive ? "text-[#2F4A3A]" : "text-[#8A958E]"}`}>
      {({ isActive }) => (
        <>
          <span className={`grid h-7 w-12 place-items-center rounded-full transition-colors ${isActive ? "bg-[#EAF0E6]" : ""}`}><Icon size={20} /></span>
          {label}
        </>
      )}
    </NavLink>
  );
}

function BottomSOS() {
  return (
    <NavLink to="/sos" data-testid="bottom-nav-sos" aria-label="SOS envie"
      className="flex flex-col items-center justify-end gap-1 pb-2 text-[11px] font-semibold text-[#E8735A]">
      <span className="relative -mt-7 grid h-14 w-14 place-items-center rounded-full bg-[#E8735A] text-white shadow-[0_10px_24px_-8px_rgba(232,115,90,.8)] ring-4 ring-[#FBF7F2]">
        <span className="pulse-ring absolute inset-0 rounded-full bg-[#E8735A]" />
        <LifeBuoy size={24} className="relative" />
      </span>
      SOS
    </NavLink>
  );
}

export function BottomNav() {
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-[#E9E1D6] bg-white/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl md:hidden" data-testid="bottom-nav">
      <div className="grid grid-cols-5 items-end">
        {BOTTOM_LEFT.map((item) => <BottomItem key={item.id} {...item} />)}
        <BottomSOS />
        {BOTTOM_RIGHT.map((item) => <BottomItem key={item.id} {...item} />)}
      </div>
    </nav>
  );
}

export function Footer() {
  return (
    <footer className="relative z-10 hidden border-t border-[#E9E1D6] py-8 text-center text-xs text-[#8A958E] md:block">
      Douceur — un compagnon bienveillant pour reprendre le contrôle sur le sucre. Ne remplace pas un avis médical. · <Link to="/tarifs" className="underline">Tarifs</Link> · <Link to="/support" className="underline">Aide</Link> · <Link to="/confidentialite" className="underline">Confidentialité</Link> · <Link to="/conditions" className="underline">Conditions</Link>
      <div className="mt-3 flex justify-center"><InstallButton testid="footer-install-btn" className="inline-flex items-center gap-2 rounded-full border border-[#E9E1D6] bg-white px-4 py-2 text-xs font-semibold text-[#2F4A3A] transition-colors hover:bg-[#EAF0E6]" /></div>
    </footer>
  );
}
